// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ChevronDown, FileText, FolderOpen } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { EtlInputFileDetail } from "@/api/hooks/use-imports";
import { CodeHighlight } from "@/components/shared/code-highlight";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";

interface TransformationFilesTabProps {
  inputFiles: EtlInputFileDetail[];
}

function FileCard({ file }: { file: EtlInputFileDetail }) {
  const { t } = useTranslation("back-office");
  const forcedFixJson = useMemo(() => (file.forcedFix ? JSON.stringify(file.forcedFix) : ""), [file.forcedFix]);
  const loadExtrasJson = useMemo(() => (file.loadExtras ? JSON.stringify(file.loadExtras) : ""), [file.loadExtras]);
  const checkDateJson = useMemo(() => (file.checkDate ? JSON.stringify(file.checkDate) : ""), [file.checkDate]);

  const hasExtras = file.regex || file.forcedFix || file.loadExtras || file.validateColumns?.length || file.checkDate;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-muted/50 transition-colors data-[state=open]:rounded-b-none">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{file.description}</p>
          <p className="text-xs text-muted-foreground">
            {file.exampleFileName} ({file.fileType})
          </p>
        </div>
        <Badge variant="outline" className="shrink-0 font-mono text-xs">
          {file.targetName}
        </Badge>
        {hasExtras && (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
        )}
      </CollapsibleTrigger>
      {hasExtras && (
        <CollapsibleContent>
          <div className="grid grid-cols-[160px_1fr] items-center gap-y-2 border border-t-0 rounded-b-lg p-3 pl-10 text-sm bg-muted/30">
            <span className="text-muted-foreground">{t("transformations.detail.fields.variesPerStore")}</span>
            <Switch checked={file.variesPerStore ?? false} className="pointer-events-none" />
            {file.regex && (
              <>
                <span className="text-muted-foreground">{t("transformations.detail.fields.regex")}</span>
                <CodeHighlight value={file.regex} lang="regexp" />
              </>
            )}
            {file.validateColumns && file.validateColumns.length > 0 && (
              <>
                <span className="text-muted-foreground">{t("transformations.detail.fields.validateColumns")}</span>
                <ul className="list-disc list-inside font-mono text-xs space-y-0.5">
                  {file.validateColumns.map((col) => (
                    <li key={col}>{col}</li>
                  ))}
                </ul>
              </>
            )}
            {file.forcedFix && Object.keys(file.forcedFix).length > 0 && (
              <>
                <span className="text-muted-foreground">{t("transformations.detail.fields.forcedFix")}</span>
                <CodeHighlight value={forcedFixJson} lang="json" />
              </>
            )}
            {file.loadExtras && Object.keys(file.loadExtras).length > 0 && (
              <>
                <span className="text-muted-foreground">{t("transformations.detail.fields.loadExtras")}</span>
                <CodeHighlight value={loadExtrasJson} lang="json" />
              </>
            )}
            {file.checkDate && Object.keys(file.checkDate).length > 0 && (
              <>
                <span className="text-muted-foreground">{t("transformations.detail.fields.checkDate")}</span>
                <CodeHighlight value={checkDateJson} lang="json" />
              </>
            )}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

export function TransformationFilesTab({ inputFiles }: TransformationFilesTabProps) {
  const { t } = useTranslation("back-office");

  if (inputFiles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">{t("transformations.detail.fields.noFiles")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-2 p-6">
      {inputFiles.map((file) => (
        <FileCard key={file.targetName} file={file} />
      ))}
    </div>
  );
}
